import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';

export default function Navbar() {
  const { user, logout } = useAuth();
  const { lang, toggleLang, t } = useLang();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [bouncing, setBouncing] = useState(false);

  const handleLogoClick = () => { setBouncing(true); setTimeout(() => setBouncing(false), 600); };
  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/dashboard', label: t('nav_inspect'), icon: '🔍', roles: ['inspector', 'admin'] },
    { path: '/my-inspections', label: t('nav_history'), icon: '📋', roles: ['inspector', 'admin'] },
    { path: '/packer-report', label: t('nav_packer_report'), icon: '📦', roles: ['inspector', 'admin'] },
    { path: '/reports', label: t('nav_reports'), icon: '📊', roles: ['admin'] },
    { path: '/settings', label: t('nav_settings'), icon: '⚙️', roles: ['admin'] },
  ];

  const visibleLinks = navLinks.filter(l => l.roles.includes(user?.role));

  return (
    <nav className="bg-white shadow-lg border-b-4 border-orange-400 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/dashboard" onClick={handleLogoClick} className="flex items-center">
              <img
                src="/aksun-logo.png"
                alt="Aksun"
                className={`h-10 w-auto ${bouncing ? 'fruit-bounce' : ''}`}
              />
            </Link>
            <div className="hidden md:flex ml-6 space-x-1">
              {visibleLinks.map(link => (
                <Link key={link.path} to={link.path}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                    isActive(link.path) ? 'bg-orange-100 text-orange-700 shadow-sm' : 'text-gray-600 hover:bg-orange-50 hover:text-orange-600'
                  }`}>
                  {link.icon} {link.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* Language toggle */}
            <button
              onClick={toggleLang}
              className="px-3 py-2 rounded-full text-sm font-bold bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors touch-active"
              title={lang === 'en' ? 'Türkçe' : 'English'}
            >
              {lang === 'en' ? '🇹🇷 TR' : '🇬🇧 EN'}
            </button>

            <div className="hidden md:flex items-center bg-green-50 px-3 py-1.5 rounded-full">
              <span className="text-sm font-semibold text-green-700">🍊 {user?.username}</span>
              <span className="ml-2 text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full font-bold uppercase">{user?.role}</span>
            </div>
            <button onClick={logout}
              className="hidden md:block bg-red-50 text-red-600 px-4 py-2 rounded-full text-sm font-semibold hover:bg-red-100 transition-colors">
              {t('logout')}
            </button>

            {/* Mobile menu button */}
            <button onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden p-3 rounded-xl text-gray-600 active:bg-orange-50 touch-active" style={{ minWidth: '48px', minHeight: '48px' }}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {menuOpen
                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                }
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-orange-100 fade-in">
          <div className="px-4 py-3 space-y-2">
            <div className="flex items-center bg-green-50 px-4 py-3 rounded-xl mb-2">
              <span className="text-sm font-semibold text-green-700">🍊 {user?.username}</span>
              <span className="ml-2 text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full font-bold uppercase">{user?.role}</span>
            </div>
            {visibleLinks.map(link => (
              <Link key={link.path} to={link.path} onClick={() => setMenuOpen(false)}
                className={`block px-4 py-4 rounded-xl text-base font-semibold transition-all touch-active ${
                  isActive(link.path) ? 'bg-orange-100 text-orange-700' : 'text-gray-600 active:bg-orange-50'
                }`}>
                {link.icon} {link.label}
              </Link>
            ))}
            <button onClick={() => { logout(); setMenuOpen(false); }}
              className="w-full text-left px-4 py-4 rounded-xl text-base font-semibold text-red-600 active:bg-red-50 touch-active">
              🚪 {t('logout')}
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
